import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { isFullScreen, toggleFullscreen } from './fullscreen';

export function useDebounce(value, delay) {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(
        () => {
            // Update debounced value after delay
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);
            return () => {
                clearTimeout(handler);
            };
        },
        [value, delay] // Only re-call effect if value or delay changes
    );
    return debouncedValue;
}

export function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

export function useWindowSize(debounce) {
    const [size, setSize] = useState([0, 0]);

    useLayoutEffect(() => {
        let timeoutId;

        const handleResize = () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                setSize([window.innerWidth, window.innerHeight]);
            }, debounce);
        };

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [debounce]);

    return size;
}

export function useFullscreen() {
    const [isFullscreen, setInFullscreen] = useState(isFullScreen());
    useLayoutEffect(() => {
        function updateSize() {
            setInFullscreen(isFullScreen());
        }
        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    return [isFullscreen, toggleFullscreen];
}

export function useLocalStorage(key, initialValue) {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState(() => {
        // Get from local storage by key
        const item = window.localStorage.getItem(key);
        return item ? item : initialValue;
    });

    const storedValueRef = useRef();

    useEffect(() => {
        storedValueRef.current = storedValue;
    }, [storedValue]);

    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = useCallback((value) => {
        // Allow value to be a function so we have same API as useState
        const valueToStore =
            value instanceof Function ? value(storedValueRef.current) : value;
        // Save state
        setStoredValue(valueToStore);
        // Save to local storage
        window.localStorage.setItem(key, valueToStore);
    }, [key]);
    return [storedValue, setValue];
}

export function useIsMounted() {
    const isMounted = useRef(false)

    useEffect(() => {
        isMounted.current = true

        return () => {
            isMounted.current = false
        }
    }, [])

    return useCallback(() => isMounted.current, [])
}

export function useSetInterval(fn, delay = 1000) {
    useEffect(() => {
        const interval = setInterval(fn, delay);
        return () => clearInterval(interval);
    }, [fn, delay]);
}

export function usePageVisibility() {
    const [isVisible, setIsVisible] = useState(!document.hidden);

    useEffect(() => {
        function handleVisibilityChange() {
            setIsVisible(document.visibilityState === 'visible');
        }

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    return isVisible;
}